/**
 * /workbook-view/:shareId — public read-only view of a shared workbook.
 *
 * Anyone with the link can open it in a browser. No signup, no editing.
 * We render the same information the user saw when they filled it in,
 * plus a small "created by MyCentennialPlan" footer and a link back to
 * the calculator.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { fetchReadinessShare } from "@/lib/api";

const TB_LABEL = {
  vision: "Retirement vision",
  budget: "Budget & cash flow",
  debt: "Debt management",
  emergency: "Emergency savings",
  retirement: "Retirement savings",
  social: "Social Security",
  healthcare: "Healthcare",
  investments: "Investments",
  taxes: "Taxes & withdrawals",
  estate: "Estate & protection",
};

const usd = (n) => `$${(Number(n) || 0).toLocaleString()}`;
const sum = (o) => Object.values(o || {}).reduce((a, v) => a + (Number(v) || 0), 0);
const has = (v) => v != null && String(v).trim() !== "" && !(Array.isArray(v) && v.length === 0);

const tier = (s) =>
  s >= 80 ? "Strong Foundation" :
  s >= 60 ? "Building Momentum" :
  s >= 40 ? "Important Work Ahead" :
  "You've Begun";

export default function WorkbookView() {
  const { shareId } = useParams();
  useSEO({
    title: "Shared readiness workbook · Centennial",
    description: "A shared, read-only Centennial retirement readiness workbook.",
    canonicalPath: `/workbook-view/${shareId}`,
  });

  const [state, setState] = useState({ loading: true, err: "", w: null });

  useEffect(() => {
    setState({ loading: true, err: "", w: null });
    fetchReadinessShare(shareId)
      .then((d) => setState({ loading: false, err: "", w: d.workbook || {} }))
      .catch((e) =>
        setState({ loading: false, err: e?.response?.status === 404 ? "Link not found." : "Could not load this workbook.", w: null }),
      );
  }, [shareId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#2B5339]" />
      </div>
    );
  }
  if (state.err) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C26747] mb-2">
            {state.err}
          </div>
          <p className="text-sm text-[#5C6B62]">
            Share links may expire or be typed incorrectly. Ask the sender to generate a fresh one.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#2B5339] hover:underline"
          >
            Go to Centennial home →
          </Link>
        </div>
      </div>
    );
  }

  const w = state.w;
  const total = Object.values(w.scores || {}).reduce((a, v) => a + (Number(v) || 0), 0);
  const inc = sum(w.monthly?.income);
  const exp = sum(w.monthly?.expense);
  const retIn = sum(w.income?.sources);
  const retSp = sum(w.income?.spending);
  const have = sum(w.money?.have);
  const owe = sum(w.money?.owe);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8">
          <ArrowLeft size={14} /> Centennial home
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] inline-flex items-center gap-2 mb-2">
          <ShieldCheck size={12} /> Read-only shared workbook
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] leading-tight">
          {w.meta?.name || "Retirement Readiness Workbook"}
        </h1>
        <p className="mt-3 text-sm text-[#5C6B62]">
          {[
            w.meta?.today && `Prepared ${w.meta.today}`,
            w.meta?.target_year && `Target retirement year ${w.meta.target_year}`,
            w.meta?.target_age && `Target retirement age ${w.meta.target_age}`,
          ].filter(Boolean).join(" · ")}
        </p>

        {/* Score card */}
        <section className="mt-8 bg-white border-2 border-[#2B5339] rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
            Readiness score
          </div>
          <div className="font-mono-num text-5xl font-medium text-[#1C2B23] leading-none mt-1">
            {total} <span className="text-[#5C6B62] text-2xl">/ 100</span>
          </div>
          <div className="text-lg text-[#1C2B23] mt-1">{tier(total)}</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {Object.entries(w.scores || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[#F0EFEA] py-1">
                <span className="text-[#5C6B62]">{TB_LABEL[k] || k}</span>
                <span className="font-mono-num text-[#1C2B23]">{v || 0} / 10</span>
              </div>
            ))}
          </div>
        </section>

        {/* Money & monthly */}
        <Section title="Money today">
          <Row label="Approx. money I have" value={usd(have)} />
          <Row label="Approx. money I owe" value={usd(owe)} />
          <Row label="Net" value={usd(have - owe)} bold />
          {has(w.money?.debt_biggest_concern) && <Note label="Biggest debt concern">{w.money.debt_biggest_concern}</Note>}
          {has(w.money?.debt_first_action) && <Note label="First action">{w.money.debt_first_action}</Note>}
        </Section>

        <Section title="Monthly cash flow">
          <Row label="Income" value={usd(inc)} />
          <Row label="Expenses" value={usd(exp)} />
          <Row label="Available" value={usd(inc - exp)} bold />
        </Section>

        <Section title="Retirement income & spending">
          <Row label="Monthly retirement income" value={usd(retIn)} />
          <Row label="Monthly retirement spending" value={usd(retSp)} />
          <Row label="Difference" value={usd(retIn - retSp)} bold />
          {has(w.income?.plan_state) && <Note label="Plan state">{w.income.plan_state}</Note>}
        </Section>

        {/* Vision + life */}
        {(has(w.vision?.retire_age) || has(w.vision?.where) || has(w.vision?.activities) || has(w.vision?.three_things)) && (
          <Section title="Vision">
            {has(w.vision?.retire_age) && <Note label="Retire at age">{w.vision.retire_age}</Note>}
            {has(w.vision?.where) && <Note label="Where">{w.vision.where.join(", ")}</Note>}
            {has(w.vision?.activities) && <Note label="Activities">{w.vision.activities.join(", ")}</Note>}
            {has(w.vision?.three_things) && (
              <Note label="Three things I want retirement to give me">
                <ol className="list-decimal ml-5">
                  {w.vision.three_things.filter(Boolean).map((t, i) => (<li key={i}>{t}</li>))}
                </ol>
              </Note>
            )}
          </Section>
        )}

        {/* Actions + promise + letter */}
        {(w.actions || []).some((a) => has(a?.need_to) || has(a?.first_step)) && (
          <Section title="Top five action items">
            <ol className="space-y-3 list-decimal ml-5">
              {(w.actions || []).map((a, i) =>
                has(a?.need_to) || has(a?.first_step) ? (
                  <li key={i} className="text-sm text-[#1C2B23]">
                    <div className="font-medium">{a.need_to || "—"}</div>
                    {has(a.first_step) && <div className="text-[#5C6B62]">First step: {a.first_step}</div>}
                    {has(a.by_when) && <div className="text-[#5C6B62]">By: {a.by_when}</div>}
                  </li>
                ) : null,
              )}
            </ol>
          </Section>
        )}

        {has(w.one_year_promise) && (
          <Section title="One-year promise">
            <p className="italic text-[#1C2B23]">{w.one_year_promise}</p>
            {(has(w.signature) || has(w.signature_date)) && (
              <p className="text-xs text-[#5C6B62] mt-3">
                Signed: {w.signature || "—"} · {w.signature_date || ""}
              </p>
            )}
          </Section>
        )}

        {has(w.future_you) && (
          <Section title="Dear Future Me">
            <p className="italic text-[#1C2B23]">{w.future_you}</p>
          </Section>
        )}

        <div className="mt-10 border-t border-[#E5E2DC] pt-6 text-[11px] text-[#5C6B62] leading-relaxed">
          Read-only shared view. This workbook is for educational purposes only and does not
          provide individualised financial, investment, tax, legal, Social Security, Medicare, or
          healthcare advice.
        </div>
        <p className="mt-3 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
          Built with MyCentennialPlan™ · <Link to="/" className="underline">mycentennialplan.com</Link>
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8 bg-white border border-[#E5E2DC] rounded-2xl p-6">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C6B62] mb-3">
        {title}
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </section>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between items-center border-b border-[#F0EFEA] py-1.5 last:border-0">
      <span className="text-[#5C6B62]">{label}</span>
      <span className={`font-mono-num text-[#1C2B23] ${bold ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}

function Note({ label, children }) {
  return (
    <div className="pt-2">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#2B5339]">{label}</div>
      <div className="text-[#1C2B23] mt-0.5 leading-relaxed">{children}</div>
    </div>
  );
}
